import { County, CountyCreateRequest, CountyUpdateRequest } from "@bluefalcon/clients-management-client-fetch";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button,
    FormControl,
    FormLabel,
    Input,
    IconButton,
} from "@chakra-ui/react";
import { LoadingSpinner } from "../components/base/loading-spinner/loading-spinner.comp";

type AddEditCountyModalProps = {
    stateId: string;
    isOpen: boolean;
    county?: County;
    handleAddCounty: (data: CountyCreateRequest) => Promise<void>;
    handleEditCounty: (countyId: number, data: CountyUpdateRequest) => Promise<void>;
    handleDeleteCounty: (countyId: number) => Promise<void>;
    closeModal: () => void;
};

export const AddEditCountyModal = ({
    isOpen,
    county,
    stateId,
    handleAddCounty,
    handleEditCounty,
    handleDeleteCounty,
    closeModal,
}: AddEditCountyModalProps) => {
    const [countyName, setCountyName] = useState(county?.value ?? "");
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async () => {
        try {
            setIsLoading(true);
            if (county && countyName.trim()) {
                await handleEditCounty(county.id, { id: county.id, value: countyName.trim() });
            } else if (countyName.trim()) {
                await handleAddCounty({ stateId, value: countyName.trim() });
            }
        } catch (error) { } finally {
            setIsLoading(false);
            onCloseModal();
        }
    };

    const handleDelete = async () => {
        if (county) {
            await handleDeleteCounty(county.id);
        }
        onCloseModal();
    };

    const onCloseModal = () => {
        setCountyName("");
        closeModal();
    };

    useEffect(() => {
        setCountyName(county?.value ?? "");
    }, [county])

    return (
        <Modal isOpen={isOpen} onClose={onCloseModal}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{county ? "Edit County" : "Add County"}</ModalHeader>
                <ModalCloseButton />
                {!isLoading ? <ModalBody>
                    <FormControl mb={4}>
                        <FormLabel htmlFor="countyName">County Name</FormLabel>
                        <Input
                            id="countyName"
                            value={countyName}
                            onChange={(e) => setCountyName(e.target.value)}
                            placeholder="Enter county name"
                        />
                    </FormControl>
                </ModalBody> : <LoadingSpinner />}

                <ModalFooter>
                    {county && (
                        <IconButton
                            aria-label="Delete County"
                            icon={<FontAwesomeIcon icon={faTrashCan} />}
                            variant="outline"
                            onClick={handleDelete}
                            mr="auto"
                        />
                    )}
                    <Button isDisabled={isLoading} variant="outline" onClick={onCloseModal}>
                        Close
                    </Button>
                    <Button isDisabled={!countyName.trim()} colorScheme="teal" ml={3} onClick={handleSubmit}>
                        {county ? "Edit County" : "Add County"}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
